import React, { useState, useCallback, useMemo } from 'react';
import { FaEye,FaSearch } from 'react-icons/fa';
import { Badge, Loading, EmptyState } from '../../../common';
import {
  getInitials,
  formatType,
  getRiskClassName,
} from '../../../../utils/helpers';
import { TYPE_COLORS } from '../../../../utils/constants';
import './IdentityTable.css';
import formatNumber from '../../../../utils/formatNumber';

/**
 * IdentityTable Component
 * Displays identities in a responsive table
 * Does NOT handle modal — parent does
 */
const IdentityTable = ({
  identities = [],
  loading = false,
  selectedId = null,
  onRowClick,      /* parent callback */
  onViewDetails,   /* parent callback */
}) => {
  /* ── HOOKS MUST BE CALLED FIRST ─────────────── */

  /* ── Normalize risk ────────────────────────── */
  const getSafeRisk = useCallback(
    (risk) => ({
      stage: risk?.stage || 'unknown',
      score: risk?.score ?? 0,
    }),
    []
  );

  /* ── Determine visible columns ──────────────── */
  const columnVisibility = useMemo(() => {
    const showDept = identities.some(
      (id) => id.type === 'Member' || id.type === 'Guest'
    );
    const showAge = identities.some((id) => id.type === 'Application');
    const showDarkWeb = identities.some(
      (id) => id.type === 'Member' || id.type === 'Guest'
    );

    return { department: showDept, age: showAge, darkweb:showDarkWeb };
  }, [identities]);

  /* ── Handle row click ──────────────────────── */
  const handleRowClick = useCallback(
    (identity) => {
      if (onRowClick) onRowClick(identity);
    },
    [onRowClick]
  );

  /* ── Handle view details button ─────────────── */
  const handleViewDetails = useCallback(
    (e, identity) => {
      e.stopPropagation();
      if (onViewDetails) onViewDetails(identity);
    },
    [onViewDetails]
  );

  /* ── CONDITIONAL RENDERING (after hooks) ─────── */
  if (loading) {
    return <Loading text="Loading identities…" />;
  }

  if (!identities.length) {
    return (
      <EmptyState
        title="No identities found"
        description="Try adjusting your filters or search"
      />
    );
  }

  /* ──────────────────────────────────────────── */
  return (
    <div
      className="identity-table-container"
      role="region"
      aria-label="Identities table"
    >
      <table className="identity-table" role="table">
        <thead>
          <tr>
            <th scope="col" className="th-identity">
              Identity
            </th>
            <th scope="col" className="th-type">
              Type
            </th>
            {columnVisibility.department && (
              <th scope="col" className="th-department">
                Department
              </th>
            )}
            {columnVisibility.age && (
              <th scope="col" className="th-age">
                Age
              </th>
            )}
            <th scope="col" className="th-status">
              Status
            </th>
            
            <th scope="col" className="th-risk">
              Risk
            </th>
            <th scope="col" className="th-score">
              Score
            </th>
            <th scope="col" className="th-owner">
              Owner
            </th>
            <th scope="col" className="th-actions">
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          {identities?.map((identity) => {
            const risk = getSafeRisk(identity?.risk);
            const rowId = identity?.id || identity?.objectId;

            return (
              <tr
                key={rowId}
                className={`identity-row ${
                  selectedId === rowId ? 'identity-row--selected' : ''
                }`}
                onClick={() => handleRowClick(identity)}
                role="row"
              >
                {/* ── Identity ──────────────────────── */}
                <td className="td-identity">
                  <div className="identity-cell">
                    <div
                      className="identity-avatar"
                      style={{
                        backgroundColor: `${
                          TYPE_COLORS[identity?.type] || '#999'
                        }15`,
                        color: TYPE_COLORS[identity?.type] || '#999',
                      }}
                      aria-hidden="true"
                    >
                      {getInitials(identity?.name || 'NA')}
                    </div>

                    <div className="identity-info">
                      <div className="identity-name">
                        {identity?.name || 'Unnamed'}
                      </div>
                      <div className="identity-secondary">
                        {identity?.email ||
                          identity?.upn ||
                          identity?.type ||
                          'N/A'}
                      </div>
                    </div>
                  </div>
                </td>

                {/* ── Type ──────────────────────────── */}
                <td className="td-type">
                  <Badge>{formatType(identity?.type || 'unknown')}</Badge>
                </td>

                {/* ── Department (conditional) ──────── */}
                {columnVisibility?.department && (
                  <td className="td-department">
                    <span className="cell-text">
                      {identity?.department || 'N/A'}
                    </span>
                  </td>
                )}

                {/* ── Age (conditional) ──────────────── */}
                {columnVisibility.age && (
                  <td className="td-age">
                    <span className="cell-text">{identity?.age || 'N/A'}</span>
                  </td>
                )}

                {/* ── Status ────────────────────────── */}
                <td className="td-status">
                  <span className="cell-text">
                    {identity.status || 'unknown'}
                  </span>
                </td>

                {/* ── Risk ──────────────────────────── */}
                <td className="td-risk">
                  <Badge variant={getRiskClassName(risk?.stage)}>
                    {risk?.stage}
                  </Badge>
                </td>

                {/* ── Score ─────────────────────────── */}
                <td className="td-score">
                  <span className="score-value">{formatNumber(risk?.score)}</span>
                </td>

                {/* ── Owner ─────────────────────────── */}
                <td className="td-owner">
                  <span className="cell-text">
                    {typeof identity?.owner === 'object'
                      ? identity?.owner?.name || identity?.owner?.email || 'N/A'
                      : identity?.owner || 'N/A'}
                  </span>
                </td>

                {/* ── Actions ───────────────────────── */}
                <td className="td-actions">
                  <button
                    className="action-btn"
                    onClick={(e) => handleViewDetails(e, identity)}
                    aria-label={`View details for ${identity?.name}`}
                    title="View details"
                  >
                    <FaEye />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default IdentityTable;